import { FlatList, Pressable, ScrollView, View } from 'dripsy';
import { t } from 'i18next';
import React, { useCallback, useContext } from 'react';
import { CurrenciesIcon } from '@/features/earn/components';
import { keyGenerator } from '@/utils/keyGenerator';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useFocusEffect } from '@react-navigation/native';
import { Platform } from 'react-native';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { TextBody, TextSmall } from '@/design-system/components/typography';
import { useAccountSelectorNative, WalletsByAccountLabelItem } from '@/design-system/components/accountSelector/useAccountSelectorNative';
import { ModalHeader } from '@/design-system/components/modal';
import { LayoutContext } from 'contexts/LayoutContext';

export interface AccountSelectWebScreenProps {
  onLayout: (event: any) => void;
  asset?: string;
  value?: string;
  disabledForwardBrowser?: boolean;
  onSelect: (walletId: string, wallet?: any) => void;
  onBack?: () => void;
}

export const AccountSelectWebScreen: React.FC<AccountSelectWebScreenProps> = (props) => {
  const { onLayout, asset, value, disabledForwardBrowser = false, onSelect, onBack } = props;

  const { displayWallets, walletsSortByAccountLabel, handleChangeSearchText } = useAccountSelectorNative({ asset });

  const isWeb = Platform.OS === 'web';
  const layout = useContext(LayoutContext);

  const onSelectWalletId = (walletId: string, wallet?: any) => {
    onSelect(walletId, wallet);
    onBack && onBack();
  };

  // Disable forward browser button
  useFocusEffect(
    useCallback(() => {
      if (isWeb && disabledForwardBrowser) {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
      }
    }, [])
  );

  const displayWalletsList = displayWallets.sort(
    (a, b) => Number(a.accountNumber.replace(/-/g, '')) - Number(b.accountNumber.replace(/-/g, ''))
  );

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={{ height: 70, justifyContent: 'center' }}>
        <ModalHeader
          title={t('earn-invest::glossary.select-asset')}
          closeModal={() => {
            onBack && onBack();
          }}
        />
      </View>
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={{ padding: '$7' }}>
          <View sx={{ pb: '$3' }}>
            <UniInput
              prefixIcon="search"
              wrapperSx={{ flex: 1 }}
              sx={{ width: '100%' }}
              onValueChange={(value) => handleChangeSearchText(value || '')}
              placeholder={t('unionPay::payment-instruction.glossary.search-by-asset')}
            />
          </View>
          <View sx={{ height: isWeb ? 'auto' : '80%' }}>
            {displayWallets.length > 0 && displayWalletsList.length > 0 ? (
              <FlatList
                data={displayWalletsList}
                keyExtractor={(item) => keyGenerator('accountSelector')}
                renderItem={({ item, index }: { item: WalletsByAccountLabelItem; index: number }) => (
                  <React.Fragment>
                    {(index == 0 || displayWalletsList[index - 1]?.accountNumber !== item.accountNumber) && (
                      <View sx={{ pt: '$3', pb: '$1' }}>
                        <TextSmall semiBold sx={{ color: '$secondary' }}>
                          {item.accountLabel ? t(`earn-invest::glossary.${item.accountLabel}`) : item.accountName || ' - '}
                        </TextSmall>
                        <TextSmall sx={{ color: '$textSecondary' }}>{item.accountNumber}</TextSmall>
                      </View>
                    )}
                    <Pressable
                      sx={{
                        paddingY: '$2',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: value === item.id ? '$disable' : 'transparent'
                      }}
                      onPress={() => onSelectWalletId(item.id, item)}>
                      <CurrenciesIcon asset={item.asset} textSize="textBody" textBold={false} />
                      <TextBody>{formatAmountByDecimals({ value: item.balance, decimals: item.decimals, suffix: item.asset })}</TextBody>
                    </Pressable>
                  </React.Fragment>
                )}
              />
            ) : (
              <View sx={{ flex: 1, height: '100%', paddingY: '$3', justifyContent: 'center', alignItems: 'center' }}>
                <Icon name="listZeroState" height={60} width={60} />
                <TextBody bold sx={{ textAlign: 'center', marginTop: '$3' }}>
                  {t('earn-invest::glossary.no-accounts-found')}
                </TextBody>
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
};
